import React, { useState, useEffect } from 'react';
import { MessageCircle, Mail, Phone, FileText, X, Plus, Send, ChevronDown, FileStack, PhoneCall, Bot, Inbox as InboxIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SMSConversationView from './SMSConversationView';
import { EmailConversationView } from './EmailConversationView';
import { EmailNeedsAssignment } from './inbox/EmailNeedsAssignment';
import { config } from '@/src/config';
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface Communication {
  id: number;
  referral_id: string;
  provider_id: string;
  provider_name: string;
  method_type: 'sms' | 'email' | 'fax' | 'voice';
  last_message: string;
  last_message_at: string;
  unread_count: number;
  status: 'active' | 'closed';
}

interface Provider {
  id: string;
  organization_name: string;
  primary_phone?: string;
  primary_email?: string;
}

interface CommunicationTemplate {
  id: number;
  name: string;
  description: string | null;
  template_type: string;
  communication_method: string;
  subject: string | null;
  body: string;
  variable_fields: string[];
  is_active: boolean;
}

interface UnifiedCommunicationInboxProps {
  // For My Providers page - show all communications for a provider
  providerId?: string;
  providerName?: string;

  // For Case Workspace - show all communications for a referral
  referralId?: string | number;

  // General inbox mode - show all communications
  inboxMode?: boolean;
}

export const UnifiedCommunicationInbox: React.FC<UnifiedCommunicationInboxProps> = ({
  providerId,
  providerName,
  referralId,
  inboxMode = false
}) => {
  const { fmt } = useFormattedDate();
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [selectedComm, setSelectedComm] = useState<Communication | null>(null);
  const [activeMethod, setActiveMethod] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [newMessageType, setNewMessageType] = useState<'sms' | 'email' | 'phone' | 'voice_ai'>('email');
  const [newMessageContent, setNewMessageContent] = useState('');
  const [newMessageSubject, setNewMessageSubject] = useState('');
  const [newMessageRecipient, setNewMessageRecipient] = useState('');
  const [callNotes, setCallNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    fetchCommunications();
    fetchTemplates();
    if (referralId) {
      fetchMatchedProviders();
    }
    // Poll for updates every 15 seconds
    const interval = setInterval(fetchCommunications, 15000);
    return () => clearInterval(interval);
  }, [providerId, referralId, activeMethod]);

  const fetchCommunications = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      let url = `${config.api.baseUrl}/api/communications?`;

      if (providerId) url += `provider_id=${providerId}&`;
      if (referralId) url += `referral_id=${referralId}&`;
      if (activeMethod !== 'all') url += `method=${activeMethod}&`;

      const response = await authFetch(url);

      if (response.ok) {
        const data = await response.json();
        setCommunications(data.communications || []);
      }
    } catch (err) {
      console.error('Error fetching communications:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchedProviders = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      // Fetch referral details to get matched providers
      const response = await authFetch(`${config.api.baseUrl}/api/referrals/${referralId}`);

      if (response.ok) {
        const data = await response.json();
        // Get matched providers from the referral
        const matchedProviders = data.matched_providers || data.matchedProviders || [];
        if (matchedProviders.length > 0) {
          setProviders(matchedProviders.map((p: any) => ({
            id: p.provider_id || p.id,
            organization_name: p.organization_name || p.provider_name || 'Unknown Provider',
            primary_phone: p.phone || p.primary_phone,
            primary_email: p.email || p.primary_email,
          })));
        }
      }
    } catch (err) {
      console.error('Error fetching matched providers:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.weaveinApi.baseUrl}/api/communication-templates?is_active=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data || []);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const applyTemplate = (template: CommunicationTemplate) => {
    setNewMessageSubject(template.subject || '');
    setNewMessageContent(template.body || '');
    setShowTemplates(false);
  };

  const filteredTemplates = templates.filter(t => {
    if (newMessageType === 'email') return t.communication_method === 'email';
    if (newMessageType === 'sms') return t.communication_method === 'sms';
    if (newMessageType === 'voice_ai') return t.communication_method === 'voice' || t.template_type === 'voice_workflow';
    return true;
  });

  const handleSendMessage = async () => {
    // For phone calls, we just need notes; for others, we need content
    if (newMessageType !== 'phone' && newMessageType !== 'voice_ai' && !newMessageContent.trim()) return;
    if (!selectedProvider && !providerId) return;

    setSending(true);
    try {
      const token = localStorage.getItem('auth_token');
      const targetProviderId = selectedProvider?.id || providerId;

      // Map frontend type to backend channel
      const channelMap: Record<string, string> = {
        'email': 'email',
        'sms': 'sms',
        'phone': 'phone',
        'voice_ai': 'voice_ai'
      };

      // Create communication record
      const response = await authFetch(`${config.api.baseUrl}/api/service-referrals/${referralId}/communicate`, {
        method: 'POST',
        jsonBody: {
          provider_id: targetProviderId,
          channel: channelMap[newMessageType] || newMessageType,
          subject: newMessageSubject || `Regarding Referral #${referralId}`,
          content: newMessageType === 'phone' ? callNotes || 'Manual phone call logged' : newMessageContent,
          recipient: newMessageRecipient || selectedProvider?.primary_email || selectedProvider?.primary_phone,
        }
      });

      if (response.ok) {
        // Reset form and refresh
        setNewMessageContent('');
        setNewMessageSubject('');
        setNewMessageRecipient('');
        setCallNotes('');
        setShowNewMessage(false);
        setSelectedProvider(null);
        fetchCommunications();

        // For phone calls, show success message
        if (newMessageType === 'phone') {
          alert('Phone call logged successfully');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'sms': return <MessageCircle className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'fax': return <FileText className="w-4 h-4" />;
      case 'voice': return <Phone className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'sms': return 'bg-blue-100 text-blue-800';
      case 'email': return 'bg-purple-100 text-purple-800';
      case 'fax': return 'bg-green-100 text-green-800';
      case 'voice': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const totalUnread = communications.reduce((sum, comm) => sum + comm.unread_count, 0);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Communications</h2>
            {providerId && providerName && (
              <p className="text-sm text-gray-600">Conversations with {providerName}</p>
            )}
            {referralId && (
              <p className="text-sm text-gray-600">All communications for this referral</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {totalUnread > 0 && (
              <Badge className="bg-red-500 text-white">
                {totalUnread} unread
              </Badge>
            )}
            <Button
              onClick={() => setShowNewMessage(true)}
              className="flex items-center gap-2 bg-v2accent hover:bg-v2accent/90 text-white"
              size="sm"
            >
              <Plus className="w-4 h-4" />
              New Message
            </Button>
          </div>
        </div>

        {/* Method Filters */}
        <div className="flex gap-2 mt-4">
          <Button
            variant={activeMethod === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveMethod('all')}
            className="flex items-center gap-1"
          >
            All
          </Button>
          <Button
            variant={activeMethod === 'sms' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveMethod('sms')}
            className="flex items-center gap-1"
          >
            <MessageCircle className="w-3 h-3" />
            SMS
          </Button>
          <Button
            variant={activeMethod === 'email' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveMethod('email')}
            className="flex items-center gap-1"
          >
            <Mail className="w-3 h-3" />
            Email
          </Button>
          <Button
            variant={activeMethod === 'fax' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveMethod('fax')}
            className="flex items-center gap-1"
          >
            <FileText className="w-3 h-3" />
            Fax
          </Button>
          <Button
            variant={activeMethod === 'voice' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveMethod('voice')}
            className="flex items-center gap-1"
          >
            <Phone className="w-3 h-3" />
            Voice
          </Button>
          <Button
            variant={activeMethod === 'needs_assignment' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveMethod('needs_assignment')}
            className="flex items-center gap-1"
          >
            <InboxIcon className="w-3 h-3" />
            Needs Assignment
          </Button>
        </div>
      </div>

      {/* New Message Form */}
      {showNewMessage && (
        <div className="border-b border-gray-200 p-4 bg-blue-50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">New Communication</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowNewMessage(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-4">
            {/* Message Type Selection */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={newMessageType === 'email' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setNewMessageType('email')}
                className="flex items-center gap-1"
              >
                <Mail className="w-3 h-3" />
                Email
              </Button>
              <Button
                variant={newMessageType === 'sms' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setNewMessageType('sms')}
                className="flex items-center gap-1"
              >
                <MessageCircle className="w-3 h-3" />
                SMS
              </Button>
              <Button
                variant={newMessageType === 'phone' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setNewMessageType('phone')}
                className="flex items-center gap-1"
              >
                <PhoneCall className="w-3 h-3" />
                Phone Call
              </Button>
              <Button
                variant={newMessageType === 'voice_ai' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setNewMessageType('voice_ai')}
                className="flex items-center gap-1"
              >
                <Bot className="w-3 h-3" />
                Voice AI
              </Button>

              {/* Template Selection Button - show for email, sms, and voice_ai */}
              {newMessageType !== 'phone' && filteredTemplates.length > 0 && (
                <div className="ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="flex items-center gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <FileStack className="w-3 h-3" />
                    Use Template
                    <ChevronDown className={`w-3 h-3 transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              )}
            </div>

            {/* Template Dropdown */}
            {showTemplates && filteredTemplates.length > 0 && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm font-medium text-purple-800 mb-2">Select a template:</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {filteredTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => applyTemplate(template)}
                      className="w-full text-left p-2 rounded bg-white border border-purple-100 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                    >
                      <p className="font-medium text-sm text-gray-900">{template.name}</p>
                      {template.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Provider Selection */}
            {providers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Provider</label>
                <div className="relative">
                  <select
                    value={selectedProvider?.id || ''}
                    onChange={(e) => {
                      const provider = providers.find(p => p.id === e.target.value);
                      setSelectedProvider(provider || null);
                      if (provider) {
                        setNewMessageRecipient(
                          newMessageType === 'email'
                            ? provider.primary_email || ''
                            : provider.primary_phone || ''
                        );
                      }
                    }}
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a provider...</option>
                    {providers.map(p => (
                      <option key={p.id} value={p.id}>{p.organization_name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Recipient */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {newMessageType === 'email' ? 'Email Address' : newMessageType === 'sms' ? 'Phone Number' : 'Phone Number'}
              </label>
              <input
                type={newMessageType === 'email' ? 'email' : 'tel'}
                value={newMessageRecipient}
                onChange={(e) => setNewMessageRecipient(e.target.value)}
                placeholder={newMessageType === 'email' ? 'provider@example.com' : '(555) 123-4567'}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Subject (for email) */}
            {newMessageType === 'email' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={newMessageSubject}
                  onChange={(e) => setNewMessageSubject(e.target.value)}
                  placeholder={`Regarding Referral #${referralId}`}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Message Content - for email and SMS */}
            {(newMessageType === 'email' || newMessageType === 'sms') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={newMessageContent}
                  onChange={(e) => setNewMessageContent(e.target.value)}
                  placeholder="Type your message here..."
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>
            )}

            {/* Phone Call UI */}
            {newMessageType === 'phone' && newMessageRecipient && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <PhoneCall className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-green-900">Manual Phone Call</p>
                    <p className="text-sm text-green-700 mt-1 mb-3">
                      Click the number below to call the provider, then log your notes.
                    </p>
                    <a
                      href={`tel:${newMessageRecipient}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Call {newMessageRecipient}
                    </a>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Call Notes (optional)</label>
                      <textarea
                        value={callNotes}
                        onChange={(e) => setCallNotes(e.target.value)}
                        placeholder="Enter notes about the call..."
                        rows={3}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Phone Call - no number yet */}
            {newMessageType === 'phone' && !newMessageRecipient && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  Please select a provider or enter a phone number above to make a call.
                </p>
              </div>
            )}

            {/* Voice AI Call Info */}
            {newMessageType === 'voice_ai' && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Bot className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-purple-900">Voice AI Call</p>
                    <p className="text-sm text-purple-700 mt-1">
                      This will initiate an automated outbound call to the provider using Voice AI.
                      The AI will use the referral acceptance workflow to allow the provider
                      to accept or decline the referral by pressing phone keys.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Send Button */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowNewMessage(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={
                  sending ||
                  (newMessageType === 'email' && !newMessageContent.trim()) ||
                  (newMessageType === 'sms' && !newMessageContent.trim()) ||
                  !newMessageRecipient
                }
                className={`flex items-center gap-2 ${
                  newMessageType === 'phone' ? 'bg-v2accent hover:bg-v2accent/90' :
                  newMessageType === 'voice_ai' ? 'bg-purple-600 hover:bg-purple-700' :
                  'bg-v2accent hover:bg-v2accent/90'
                } text-white`}
              >
                {sending ? (
                  <>Processing...</>
                ) : newMessageType === 'phone' ? (
                  <>
                    <PhoneCall className="w-4 h-4" />
                    Log Call
                  </>
                ) : newMessageType === 'voice_ai' ? (
                  <>
                    <Bot className="w-4 h-4" />
                    Start Voice AI Call
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeMethod === 'needs_assignment' ? (
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <EmailNeedsAssignment />
        </div>
      ) : (
      <div className="flex-1 flex overflow-hidden">
        {/* Conversation List */}
        <div className={`${selectedComm ? 'w-1/3' : 'w-full'} border-r border-gray-200 overflow-y-auto bg-gray-50`}>
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              Loading conversations...
            </div>
          ) : communications.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 font-medium">No conversations yet</p>
              <p className="text-gray-500 text-sm mt-1">
                Click "New Message" to start a conversation
              </p>
              <Button
                onClick={() => setShowNewMessage(true)}
                className="mt-4 flex items-center gap-2 mx-auto"
                variant="outline"
              >
                <Plus className="w-4 h-4" />
                New Message
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {communications.map((comm) => (
                <button
                  key={comm.id}
                  onClick={() => setSelectedComm(comm)}
                  className={`w-full p-4 text-left transition-colors relative ${
                    selectedComm?.id === comm.id
                      ? 'bg-white border-l-4 border-blue-600'
                      : 'hover:bg-white border-l-4 border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded ${getMethodColor(comm.method_type)}`}>
                        {getMethodIcon(comm.method_type)}
                      </div>
                      <span className="font-medium text-gray-900">{comm.provider_name}</span>
                    </div>
                    {comm.unread_count > 0 && (
                      <Badge className="bg-red-500 text-white text-xs">
                        {comm.unread_count}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-1">
                    {comm.last_message.length > 80
                      ? `${comm.last_message.substring(0, 80)}...`
                      : comm.last_message}
                  </p>
                  <p className="text-xs text-gray-400">
                    {fmt.dateTime(comm.last_message_at)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conversation View */}
        {selectedComm && (
          <div className="flex-1 flex flex-col bg-white">
            {/* Conversation Header */}
            <div className="border-b border-gray-200 p-4 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getMethodColor(selectedComm.method_type)}`}>
                  {getMethodIcon(selectedComm.method_type)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedComm.provider_name}</h3>
                  <p className="text-sm text-gray-600 capitalize">
                    {selectedComm.method_type} Conversation
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedComm(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Conversation Content - Routed by method type */}
            <div className="flex-1 overflow-hidden">
              {selectedComm.method_type === 'sms' && (
                <SMSConversationView
                  referralId={selectedComm.referral_id}
                  providerId={selectedComm.provider_id}
                  providerName={selectedComm.provider_name}
                  providerPhone="" // Will fetch from backend
                  onClose={() => setSelectedComm(null)}
                />
              )}

              {selectedComm.method_type === 'email' && (
                <EmailConversationView
                  referralId={selectedComm.referral_id}
                  providerId={selectedComm.provider_id}
                  providerName={selectedComm.provider_name}
                  providerEmail="" // Will fetch from backend
                />
              )}

              {selectedComm.method_type === 'fax' && (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3" />
                  <p className="font-medium">Fax History View</p>
                  <p className="text-sm mt-1">Coming soon - Sent/received fax documents</p>
                </div>
              )}

              {selectedComm.method_type === 'voice' && (
                <div className="p-8 text-center text-gray-500">
                  <Phone className="w-12 h-12 mx-auto mb-3" />
                  <p className="font-medium">Voice Call Transcripts</p>
                  <p className="text-sm mt-1">Coming soon - AI-transcribed call history with OpenAI</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};
